import { inject, Injectable } from '@angular/core';
import { concatMap } from 'rxjs/operators';
import { catchError, EMPTY, Observable, Subject, tap } from 'rxjs';
import { DialogService } from 'primeng/dynamicdialog';
import { AnnouncementDialog } from '../../pages/admin-panel/admin-panel-announcements/components/announcement-dialog';
import { AnnouncementConfirmationRequirement } from '../enums/announcement-confirmation-requirement.enum';
import { AuthService } from '../../core/services/auth.service';
import { AnnouncementUser } from '../models/announcement-user';
import { UserApi } from './api/user.api';

@Injectable({ providedIn: 'root' })
export class AnnouncementService {
  readonly #authService = inject(AuthService);
  readonly #userApi = inject(UserApi);

  private queue$ = new Subject<any>();

  constructor(private dialogService: DialogService) {
    this.initQueueListener();
  }

  public checkAndShowAnnouncements(): void {
    this.#userApi
      .getAnnouncementsForUser$()
      .pipe(
        tap((announcementUser: AnnouncementUser) => {
          if (!announcementUser?.announcementsEmailConsent) {
            this.#authService.showAnnouncemenctsEmailNotificationsDialog();
          }
          if (announcementUser?.announcements?.length > 0) {
            this.#showAnnouncements(announcementUser?.announcements);
          }
        }),
        catchError(() => {
          return EMPTY;
        }),
      )
      .subscribe();
  }

  #showAnnouncements(list: any[]): void {
    list.forEach((ann) => this.queue$.next(ann));
  }

  private initQueueListener() {
    this.queue$
      .pipe(
        // concatMap czeka, aż poprzedni Observable się zakończy
        concatMap((ann) => this.#openDialogAsObservable(ann)),
      )
      .subscribe();
  }

  #openDialogAsObservable(ann: any): Observable<any> {
    return new Observable((observer) => {
      const ref = this.dialogService.open(AnnouncementDialog, {
        inputValues: {
          header: ann.title,
          content: ann.content,
          id: ann.id,
          requiresConfirmation:
            ann.requiresConfirmation ===
            AnnouncementConfirmationRequirement.TAK,
        },
        showHeader: false,
        modal: true,
        closable:
          ann.requiresConfirmation === AnnouncementConfirmationRequirement.NIE,
        dismissableMask:
          ann.requiresConfirmation === AnnouncementConfirmationRequirement.NIE,
        closeOnEscape:
          ann.requiresConfirmation === AnnouncementConfirmationRequirement.NIE,
        width: '55vw',
        appendTo: 'body',
        breakpoints: {
          '960px': '90vw',
          '640px': '90vw',
        },
      });

      if (!ref) {
        observer.next(false);
        observer.complete();
        return;
      }

      // Flaga pomocnicza, żeby nie wywołać next() dwa razy
      let completed = false;

      const finalize = () => {
        if (!completed) {
          completed = true;
          setTimeout(() => {
            observer.next(true);
            observer.complete();
          }, 200);
        }
      };

      // Obsługa zamknięcia przez ref.close()
      ref.onClose.pipe(tap(() => finalize())).subscribe();

      // Obsługa zniszczenia komponentu (np. kliknięcie poza dialogiem, ESC)
      // jeśli onClose nie zadziała
      ref.onDestroy.pipe(tap(() => finalize())).subscribe();
    });
  }
}
